import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  FileUp,
  Info,
  Loader2,
  Sparkles,
  Upload,
  XCircle,
} from "lucide-react";
import { buildHypertensionFormStyles } from "./hypertension/styleHelpers";
import {
  HYPERTENSION_STAGES,
  HYPERTENSION_SERVICE_NAME,
  getStage,
} from "@/lib/hypertensionCaseFinding";
import { links } from "@/lib/linksRegistry";
import { ClinicalReferencesCollapsible } from "./ClinicalReferencesCollapsible";
import { useTitanApp } from "@/hooks/useTitanApp";
import { updatePatientSurgery } from "@/dispatchers/patientDispatchers";

// ── ABPM Reference Chart ─────────────────────────────────────────────────────
// Uses NICE ABPM daytime-average thresholds, which differ from clinic BP bands.
function AbpmReferenceChart({
  isDarkMode,
  daytimeSystolic,
  daytimeDiastolic,
}: {
  isDarkMode: boolean;
  daytimeSystolic: string;
  daytimeDiastolic: string;
}) {
  const [expanded, setExpanded] = useState(false);

  const bands = [
    {
      key: "low",
      shortTitle: "≤89/59",
      title: "89/59 mmHg or lower",
      label: "LOW BLOOD PRESSURE",
      shortLabel: "LOW",
      bar: "bg-blue-500",
      badgeBg: isDarkMode
        ? "bg-blue-500/30 text-blue-200"
        : "bg-blue-500 text-white",
      match: (s: number, d: number) => s <= 89 || d <= 59,
    },
    {
      key: "normal",
      shortTitle: "90/60 – 134/84",
      title: "Between 90/60 mmHg and 134/84 mmHg",
      label: "NORMAL BLOOD PRESSURE",
      shortLabel: "NORMAL",
      bar: "bg-emerald-500",
      badgeBg: isDarkMode
        ? "bg-emerald-500/30 text-emerald-200"
        : "bg-emerald-500 text-white",
      match: (s: number, d: number) =>
        s >= 90 && s <= 134 && d >= 60 && d <= 84,
    },
    {
      key: "stage1",
      shortTitle: "135/85 – 149/94",
      title: "Between 135/85 mmHg and 149/94 mmHg",
      label: "STAGE 1 HYPERTENSION",
      shortLabel: "STAGE 1",
      bar: "bg-amber-400",
      badgeBg: isDarkMode
        ? "bg-amber-400/30 text-amber-200"
        : "bg-amber-400 text-white",
      match: (s: number, d: number) =>
        (s >= 135 && s <= 149) || (d >= 85 && d <= 94),
    },
    {
      key: "stage2",
      shortTitle: "150/95 – 169/114",
      title: "150/95 mmHg and 169/114 mmHg",
      label: "STAGE 2 HYPERTENSION",
      shortLabel: "STAGE 2",
      bar: "bg-orange-500",
      badgeBg: isDarkMode
        ? "bg-orange-500/30 text-orange-200"
        : "bg-orange-500 text-white",
      match: (s: number, d: number) =>
        (s >= 150 && s <= 169) || (d >= 95 && d <= 114),
    },
    {
      key: "very-high",
      shortTitle: "≥170/115",
      title: "170/115 mmHg or higher",
      label: "VERY HIGH BLOOD PRESSURE",
      shortLabel: "VERY HIGH",
      bar: "bg-rose-600",
      badgeBg: isDarkMode
        ? "bg-rose-500/30 text-rose-200"
        : "bg-rose-600 text-white",
      match: (s: number, d: number) => s >= 170 || d >= 115,
    },
  ];

  const s = parseFloat(daytimeSystolic);
  const d = parseFloat(daytimeDiastolic);
  const avg =
    !isNaN(s) && !isNaN(d) && daytimeSystolic !== "" && daytimeDiastolic !== ""
      ? { systolic: s, diastolic: d }
      : null;
  const activeKey = avg
    ? bands.find((b) => b.match(avg.systolic, avg.diastolic))?.key
    : null;

  return (
    <div
      className={`mb-4 rounded-xl border p-3 ${
        isDarkMode
          ? "border-gray-700 bg-gray-900/40"
          : "border-gray-200 bg-gray-50"
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div
          className={`text-xs font-semibold ${
            isDarkMode ? "text-gray-200" : "text-gray-800"
          }`}
        >
          ABPM Readings
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className={`flex items-center gap-1 text-xs font-medium hover:underline ${
            isDarkMode ? "text-blue-300" : "text-blue-600"
          }`}
        >
          {expanded ? "Collapse" : "Expand"}
          {expanded ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )}
        </button>
      </div>

      {expanded ? (
        <div className="space-y-2">
          {bands.map((b) => {
            const isActive = activeKey === b.key;
            return (
              <div
                key={b.key}
                className={`flex items-stretch gap-2 rounded-md border ${
                  isActive
                    ? isDarkMode
                      ? "border-blue-500 bg-blue-900/20"
                      : "border-blue-500 bg-blue-50"
                    : isDarkMode
                      ? "border-gray-700 bg-gray-800"
                      : "border-gray-200 bg-white"
                }`}
              >
                <div className={`w-1 rounded-l-md ${b.bar}`} />
                <div className="flex-1 py-1.5 pr-2">
                  <div
                    className={`text-xs font-medium ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    {b.title}
                  </div>
                  <span
                    className={`mt-1 inline-block rounded-sm px-1.5 py-0.5 text-[10px] font-semibold tracking-wide ${b.badgeBg}`}
                  >
                    {b.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-5 gap-1.5">
          {bands.map((b) => {
            const isActive = activeKey === b.key;
            return (
              <div
                key={b.key}
                title={`${b.title} – ${b.label}`}
                className={`rounded-md border overflow-hidden ${
                  isActive
                    ? "border-blue-500 ring-1 ring-blue-500"
                    : isDarkMode
                      ? "border-gray-700"
                      : "border-gray-200"
                }`}
              >
                <div className={`h-1.5 ${b.bar}`} />
                <div
                  className={`px-1.5 py-1 ${
                    isDarkMode ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  <div
                    className={`text-[10px] font-medium leading-tight ${
                      isDarkMode ? "text-gray-100" : "text-gray-900"
                    }`}
                  >
                    {b.shortTitle}
                  </div>
                  <span
                    className={`mt-0.5 inline-block rounded-sm px-1 py-px text-[9px] font-semibold tracking-wide ${b.badgeBg}`}
                  >
                    {b.shortLabel}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {avg && activeKey && (
        <div
          className={`mt-2 text-xs ${
            isDarkMode ? "text-gray-300" : "text-gray-600"
          }`}
        >
          Daytime average ({avg.systolic}/{avg.diastolic} mmHg) falls in the
          highlighted band.
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export interface NHSHypertensionABPMFollowUpFormData {
  patientConsent: "yes" | "no" | "";
  consultationDate: string;
  consultationTime: string;

  // Service provision gate
  serviceProvided: "yes" | "no" | "";
  serviceNotProvidedReason: "could-not-tolerate" | "failed-to-return" | "";

  // Pre-checks
  previousStagesReviewed: boolean;
  monitorReturned: boolean;
  monitorReturnNotes: string;

  // ABPM results
  totalReadings: string;
  successfulReadings: string;
  daytimeAvgSystolic: string;
  daytimeAvgDiastolic: string;
  nighttimeAvgSystolic: string;
  nighttimeAvgDiastolic: string;
  twentyFourHourAvgSystolic: string;
  twentyFourHourAvgDiastolic: string;

  // Stage 2 acute symptoms triage (only used when ABPM result is Stage 2)
  stage2AcuteSymptoms: "" | "yes" | "no";

  // Diagnosis
  diagnosis:
    | "no-hypertension"
    | "stage-1-hypertension"
    | "stage-2-hypertension"
    | "severe-hypertension"
    | "white-coat"
    | "inconclusive"
    | "";
  diagnosisNotes: string;

  // Outcome / referral
  outcome:
    | "discharge-no-action"
    | "lifestyle-advice"
    | "refer-gp-routine"
    | "refer-gp-urgent"
    | "repeat-abpm"
    | "";
  outcomeNotes: string;
  referralRecipient: string;

  // Referral details
  referralTarget: "" | "gp" | "other";
  referralOtherType: "" | "ooh-gp" | "ae" | "999" | "other";
  referralOtherSpecify: string;
  referralDate: string;
  referralTime: string;

  // Patient communication
  resultsExplained: boolean;
  followUpAdviceGiven: boolean;
  patientLeafletGiven: boolean;

  // Healthy living advice
  healthyLivingAdvice: Array<
    | "diet-nutrition"
    | "sodium-salt"
    | "caffeine"
    | "alcohol"
    | "smoking"
    | "physical-activity"
    | "referral-local-healthy-living"
  >;
  healthyLivingAdviceNotes: string;

  internalNotes: string;
}

interface Props {
  isDarkMode?: boolean;
  onSave?: (data: NHSHypertensionABPMFollowUpFormData) => void;
  onComplete?: (data: NHSHypertensionABPMFollowUpFormData) => void;
  initialData?: Partial<NHSHypertensionABPMFollowUpFormData>;
  parseAbpmReportFromApi?: (data: any) => any;
  nhsHypertensionABPMFolloupFormLoading?: boolean;
  consultationStatus?: number;
}

export interface AbpmReadingsResult {
  success: boolean;
  totalReadings?: number;
  successfulReadings?: number;
  daytimeAvgSystolic?: number;
  daytimeAvgDiastolic?: number;
  nighttimeAvgSystolic?: number;
  nighttimeAvgDiastolic?: number;
  twentyFourHourAvgSystolic?: number;
  twentyFourHourAvgDiastolic?: number;
  extractionNotes?: string;
}

const defaultData: NHSHypertensionABPMFollowUpFormData = {
  patientConsent: "",
  consultationDate: new Date().toISOString().split("T")[0],
  consultationTime: new Date().toTimeString().slice(0, 5),
  serviceProvided: "",
  serviceNotProvidedReason: "",
  previousStagesReviewed: false,
  monitorReturned: false,
  monitorReturnNotes: "",
  totalReadings: "",
  successfulReadings: "",
  daytimeAvgSystolic: "",
  daytimeAvgDiastolic: "",
  nighttimeAvgSystolic: "",
  nighttimeAvgDiastolic: "",
  twentyFourHourAvgSystolic: "",
  twentyFourHourAvgDiastolic: "",
  stage2AcuteSymptoms: "",
  diagnosis: "",
  diagnosisNotes: "",
  outcome: "",
  outcomeNotes: "",
  referralRecipient: "",
  referralTarget: "",
  referralOtherType: "",
  referralOtherSpecify: "",
  referralDate: new Date().toISOString().split("T")[0],
  referralTime: new Date().toTimeString().slice(0, 5),
  resultsExplained: false,
  followUpAdviceGiven: false,
  patientLeafletGiven: false,
  healthyLivingAdvice: [],
  healthyLivingAdviceNotes: "",
  internalNotes: "",
};

export function NHSHypertensionABPMFollowUpForm({
  isDarkMode = false,
  onSave,
  onComplete,
  initialData,
  parseAbpmReportFromApi,
  nhsHypertensionABPMFolloupFormLoading = false,
  consultationStatus,
}: Props) {
  const { patientState } = useTitanApp();
  const { selectedPatientDetails, isUpdatePatientSurgeryLoading } =
    patientState || {};
  const [formData, setFormData] = useState<NHSHypertensionABPMFollowUpFormData>(
    { ...defaultData, ...initialData },
  );
  const [clinicalRefsOpen, setClinicalRefsOpen] = useState(false);

  const stage = getStage("nhs-htn-abpm-followup");
  const clinicalLinkRefs = stage.clinicalLinkRefs || [];
  const headerLinks = clinicalLinkRefs.map((ref) => links[ref]).filter(Boolean);

  // ABPM report upload + AI parsing state
  const [reportFileName, setReportFileName] = useState<string>("");
  const [reportParsing, setReportParsing] = useState<boolean>(false);
  const [reportParseStatus, setReportParseStatus] = useState<
    "idle" | "success" | "failed"
  >("idle");
  const [reportParseSummary, setReportParseSummary] = useState<string>("");

  // GP surgery on file (mocked – none initially)
  const [gpSurgery, setGpSurgery] = useState<{
    name: string;
    address: string;
    phone: string;
  } | null>(null);
  const [gpAddMode, setGpAddMode] = useState<boolean>(false);
  const [gpDraft, setGpDraft] = useState<{
    name: string;
    address: string;
    phone: string;
  }>({ name: "", address: "", phone: "" });
  const [pdsSyncing, setPdsSyncing] = useState<boolean>(false);

  const handlePdsSync = async () => {
    setPdsSyncing(true);

    await new Promise((r) => setTimeout(r, 500));
    const surgery = selectedPatientDetails?.surgery || null;
    setGpSurgery({
      name: surgery?.name || "",
      address: [
        surgery?.addressLine1,
        surgery?.addressLine2,
        surgery?.addressLine3,
        surgery?.addressLine4,
        surgery?.addressLine5,
      ]
        .filter(Boolean)
        .join(", "),
      phone: surgery?.contactTelephoneNumber || "",
    });
    setPdsSyncing(false);
    setGpAddMode(false);
  };

  // ── Utility: file → base64 ────────────────────────────────────────────────
  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // result is "data:<mime>;base64,<data>" – send the full string
        resolve(result);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  // ── Replace parseAbpmReport with this ────────────────────────────────────
  const parseAbpmReport = async (file: File) => {
    setReportFileName(file.name);
    setReportParsing(true);
    setReportParseStatus("idle");
    setReportParseSummary("");

    try {
      const base64File = await fileToBase64(file);
      const payload = {
        base64File: base64File,
        fileName: file.name,
      };
      const response = await parseAbpmReportFromApi?.(payload);
      const data = response as AbpmReadingsResult;
      if (!data || !data.success) {
        setReportParseStatus("failed");
        return;
      }
      setFormData((prev) => ({
        ...prev,
        ...(data.totalReadings !== undefined && {
          totalReadings: String(data.totalReadings),
        }),
        ...(data.successfulReadings !== undefined && {
          successfulReadings: String(data.successfulReadings),
        }),
        ...(data.daytimeAvgSystolic !== undefined && {
          daytimeAvgSystolic: String(data.daytimeAvgSystolic),
        }),
        ...(data.daytimeAvgDiastolic !== undefined && {
          daytimeAvgDiastolic: String(data.daytimeAvgDiastolic),
        }),
        ...(data.nighttimeAvgSystolic !== undefined && {
          nighttimeAvgSystolic: String(data.nighttimeAvgSystolic),
        }),
        ...(data.nighttimeAvgDiastolic !== undefined && {
          nighttimeAvgDiastolic: String(data.nighttimeAvgDiastolic),
        }),
        ...(data.twentyFourHourAvgSystolic !== undefined && {
          twentyFourHourAvgSystolic: String(data.twentyFourHourAvgSystolic),
        }),
        ...(data.twentyFourHourAvgDiastolic !== undefined && {
          twentyFourHourAvgDiastolic: String(data.twentyFourHourAvgDiastolic),
        }),
      }));
      const parts: string[] = [];
      if (data.daytimeAvgSystolic && data.daytimeAvgDiastolic)
        parts.push(
          `daytime average ${data.daytimeAvgSystolic}/${data.daytimeAvgDiastolic} mmHg`,
        );
      if (data.nighttimeAvgSystolic && data.nighttimeAvgDiastolic)
        parts.push(
          `night-time average ${data.nighttimeAvgSystolic}/${data.nighttimeAvgDiastolic} mmHg`,
        );
      if (data.twentyFourHourAvgSystolic && data.twentyFourHourAvgDiastolic)
        parts.push(
          `24-hour average ${data.twentyFourHourAvgSystolic}/${data.twentyFourHourAvgDiastolic} mmHg`,
        );

      setReportParseSummary(parts.join(", "));
      setReportParseStatus("success");
    } catch {
      setReportParseStatus("failed");
    } finally {
      setReportParsing(false);
    }
  };

  const handleChange = <K extends keyof NHSHypertensionABPMFollowUpFormData>(
    field: K,
    value: NHSHypertensionABPMFollowUpFormData[K],
  ) => setFormData((prev) => ({ ...prev, [field]: value }));

  const styles = buildHypertensionFormStyles(isDarkMode);
  const {
    card,
    sectionBadge,
    sectionTitle,
    labelCls,
    inputCls,
    selectCls,
    selectContentCls,
    radioCls,
    radioLabelCls,
    helperText,
  } = styles;

  const serviceWasProvided = formData.serviceProvided === "yes";
  const serviceNotProvided = formData.serviceProvided === "no";

  // Allow completion when consent given and service provision answered:
  // – If not provided: reason must be selected
  // – If provided: full clinical pathway must be complete
  const canSubmit =
    formData.patientConsent === "yes" &&
    formData.serviceProvided !== "" &&
    (serviceNotProvided
      ? formData.serviceNotProvidedReason !== ""
      : formData.previousStagesReviewed &&
        formData.monitorReturned &&
        formData.daytimeAvgSystolic !== "" &&
        formData.daytimeAvgDiastolic !== "" &&
        formData.referralTarget !== "");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSave) onSave(formData);
    if (onComplete) onComplete(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Service header */}
      <div
        className={`z-10 rounded-2xl p-4 shadow-lg ${
          isDarkMode
            ? "bg-rose-900/20 border border-rose-800"
            : "bg-rose-50 border border-rose-200"
        }`}
        style={{
          backgroundImage: isDarkMode
            ? "linear-gradient(180deg, rgba(159,18,57,0.3) 0%, rgba(159,18,57,0.2) 100%)"
            : "linear-gradient(180deg, rgba(254,240,242,1) 0%, rgba(254,240,242,1) 80%, rgba(254,240,242,0.98) 100%)",
        }}
      >
        <div className="flex items-start gap-3">
          <ClipboardList
            className={`w-5 h-5 mt-0.5 ${
              isDarkMode ? "text-rose-300" : "text-rose-600"
            }`}
          />
          <div className="flex-1">
            <div
              className={`text-sm font-semibold ${
                isDarkMode ? "text-rose-200" : "text-rose-900"
              }`}
            >
              {HYPERTENSION_SERVICE_NAME} – Stage 3: ABPM Follow Up
            </div>
            <p className={`mt-1 text-xs ${helperText}`}>
              Review the ABPM readings, confirm a diagnosis and agree the next
              steps with the patient.
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
              {HYPERTENSION_STAGES.map((s) => (
                <span
                  key={s.id}
                  className={`px-2 py-0.5 rounded-full border ${
                    s.number === 3
                      ? isDarkMode
                        ? "bg-rose-800 border-rose-700 text-rose-100"
                        : "bg-rose-600 border-rose-600 text-white"
                      : isDarkMode
                        ? "border-gray-700 text-gray-400"
                        : "border-gray-300 text-gray-500"
                  }`}
                >
                  Stage {s.number}: {s.shortLabel}
                </span>
              ))}
            </div>
            {headerLinks.length > 0 && (
              <ClinicalReferencesCollapsible
                links={headerLinks}
                isDarkMode={isDarkMode}
                isOpen={clinicalRefsOpen}
                onOpenChange={setClinicalRefsOpen}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Section 1: Consent ───────────────────────────────────────────── */}
      <div className={card}>
        <div className="flex items-center gap-3 mb-4">
          <div className={sectionBadge}>1</div>
          <h3 className={sectionTitle}>Patient Consent</h3>
        </div>
        <RadioGroup
          value={formData.patientConsent}
          onValueChange={(v) => handleChange("patientConsent", v as any)}
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem
              value="yes"
              id="abpmu-consent-yes"
              className={radioCls}
            />
            <label htmlFor="abpmu-consent-yes" className={radioLabelCls}>
              Yes
            </label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem
              value="no"
              id="abpmu-consent-no"
              className={radioCls}
            />
            <label htmlFor="abpmu-consent-no" className={radioLabelCls}>
              No
            </label>
          </div>
        </RadioGroup>
      </div>

      {/* ── Section 2: Service Provision ─────────────────────────────────── */}
      {formData.patientConsent === "yes" && (
        <div className={card}>
          <div className="flex items-center gap-3 mb-4">
            <div className={sectionBadge}>2</div>
            <h3 className={sectionTitle}>Service Provision</h3>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className={labelCls}>
                Was the ABPM follow-up service provided?
              </Label>
              <RadioGroup
                value={formData.serviceProvided}
                onValueChange={(v) => {
                  handleChange("serviceProvided", v as any);
                  if (v === "yes") handleChange("serviceNotProvidedReason", "");
                }}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="yes"
                    id="svc-provided-yes"
                    className={radioCls}
                  />
                  <label htmlFor="svc-provided-yes" className={radioLabelCls}>
                    Yes
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="no"
                    id="svc-provided-no"
                    className={radioCls}
                  />
                  <label htmlFor="svc-provided-no" className={radioLabelCls}>
                    No
                  </label>
                </div>
              </RadioGroup>
            </div>

            {serviceNotProvided && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className={labelCls}>
                    Reason service not provided
                  </Label>
                  <RadioGroup
                    value={formData.serviceNotProvidedReason}
                    onValueChange={(v) =>
                      handleChange("serviceNotProvidedReason", v as any)
                    }
                    className="space-y-2"
                  >
                    <div
                      className={`flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer ${
                        formData.serviceNotProvidedReason ===
                        "could-not-tolerate"
                          ? isDarkMode
                            ? "border-rose-500 bg-rose-900/20"
                            : "border-rose-400 bg-rose-50"
                          : isDarkMode
                            ? "border-gray-600"
                            : "border-gray-200"
                      }`}
                      onClick={() =>
                        handleChange(
                          "serviceNotProvidedReason",
                          "could-not-tolerate",
                        )
                      }
                    >
                      <RadioGroupItem
                        value="could-not-tolerate"
                        id="reason-tolerate"
                        className={radioCls}
                      />
                      <label
                        htmlFor="reason-tolerate"
                        className={`${radioLabelCls} cursor-pointer`}
                      >
                        Patient advised they could not tolerate ABPM
                      </label>
                    </div>
                    <div
                      className={`flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer ${
                        formData.serviceNotProvidedReason === "failed-to-return"
                          ? isDarkMode
                            ? "border-rose-500 bg-rose-900/20"
                            : "border-rose-400 bg-rose-50"
                          : isDarkMode
                            ? "border-gray-600"
                            : "border-gray-200"
                      }`}
                      onClick={() =>
                        handleChange(
                          "serviceNotProvidedReason",
                          "failed-to-return",
                        )
                      }
                    >
                      <RadioGroupItem
                        value="failed-to-return"
                        id="reason-return"
                        className={radioCls}
                      />
                      <label
                        htmlFor="reason-return"
                        className={`${radioLabelCls} cursor-pointer`}
                      >
                        Failed to return ABPM device
                      </label>
                    </div>
                  </RadioGroup>
                </div>

                {formData.serviceNotProvidedReason !== "" && (
                  <Alert
                    className={
                      isDarkMode
                        ? "bg-amber-900/20 border-amber-700 text-amber-100"
                        : "bg-amber-50 border-amber-300 text-amber-900"
                    }
                  >
                    <XCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      Service not provided. Record the reason above and complete
                      the consultation. The patient may need to be recalled for
                      a further attempt.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Sections 3–7: Only shown when service was provided ───────────── */}
      {formData.patientConsent === "yes" && serviceWasProvided && (
        <>
          {/* ── Section 3: Pre-checks ─────────────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>3</div>
              <h3 className={sectionTitle}>Pre-Checks</h3>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <Label className={labelCls}>Date</Label>
                <div className="relative">
                  <Input
                    type="date"
                    value={formData.consultationDate}
                    onChange={(e) =>
                      handleChange("consultationDate", e.target.value)
                    }
                    className={inputCls}
                  />
                  <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Time</Label>
                <Input
                  type="time"
                  value={formData.consultationTime}
                  onChange={(e) =>
                    handleChange("consultationTime", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="prev-stages"
                  checked={formData.previousStagesReviewed}
                  onCheckedChange={(v) =>
                    handleChange("previousStagesReviewed", Boolean(v))
                  }
                />
                <label htmlFor="prev-stages" className={radioLabelCls}>
                  Stage 1 &amp; Stage 2 records reviewed
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="monitor-returned"
                  checked={formData.monitorReturned}
                  onCheckedChange={(v) =>
                    handleChange("monitorReturned", Boolean(v))
                  }
                />
                <label htmlFor="monitor-returned" className={radioLabelCls}>
                  ABPM device returned in working condition
                </label>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Device return notes</Label>
                <Textarea
                  rows={2}
                  value={formData.monitorReturnNotes}
                  onChange={(e) =>
                    handleChange("monitorReturnNotes", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          {/* ── Section 4: ABPM Report Upload (AI assisted) ─────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>4</div>
              <h3 className={sectionTitle}>ABPM Report Upload</h3>
            </div>
            <p className={`mb-3 ${helperText}`}>
              Upload the ABPM report exported from the device. AI will read the
              report summary and automatically fill in the daytime and
              night-time averages below.
            </p>

            <div
              className={`rounded-xl border-2 border-dashed p-4 ${
                isDarkMode
                  ? "border-gray-600 bg-gray-800/30"
                  : "border-gray-300 bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-3">
                <FileUp
                  className={`w-5 h-5 ${
                    isDarkMode ? "text-gray-300" : "text-gray-600"
                  }`}
                />
                <div className="flex-1">
                  <Label className={labelCls}>ABPM report (PDF)</Label>
                  <Input
                    type="file"
                    accept="application/pdf,.pdf,.txt"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) parseAbpmReport(f);
                    }}
                    className={`${inputCls} mt-1`}
                  />
                  {reportFileName && (
                    <div
                      className={`mt-2 text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Selected: {reportFileName}
                    </div>
                  )}
                </div>
              </div>

              {reportParsing && (
                <div
                  className={`mt-3 flex items-center gap-2 text-sm ${
                    isDarkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <Sparkles className="w-4 h-4" />
                  AI is reading the report…
                </div>
              )}

              {!reportParsing && reportParseStatus === "success" && (
                <Alert
                  className={`mt-3 ${
                    isDarkMode
                      ? "bg-emerald-900/20 border-emerald-700 text-emerald-100"
                      : "bg-emerald-50 border-emerald-300 text-emerald-900"
                  }`}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    Report read successfully. Extracted {reportParseSummary}.
                    Values have been pre-filled in the ABPM Results section
                    below — please review before continuing.
                  </AlertDescription>
                </Alert>
              )}

              {!reportParsing && reportParseStatus === "failed" && (
                <div className="mt-3 space-y-2">
                  <Alert
                    className={
                      isDarkMode
                        ? "bg-amber-900/20 border-amber-700 text-amber-100"
                        : "bg-amber-50 border-amber-300 text-amber-900"
                    }
                  >
                    <XCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm">
                      We couldn&apos;t read the ABPM averages from this report.
                      Please check the file is the original Hingmed PDF, or
                      enter the results manually below.
                    </AlertDescription>
                  </Alert>
                  <div
                    className={`flex items-center gap-2 text-xs ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Please enter the ABPM results manually in the section below.
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Section 5: ABPM Results ─────────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>5</div>
              <h3 className={sectionTitle}>ABPM Results</h3>
            </div>
            <AbpmReferenceChart
              isDarkMode={isDarkMode}
              daytimeSystolic={formData.daytimeAvgSystolic}
              daytimeDiastolic={formData.daytimeAvgDiastolic}
            />
            <p className={`mb-4 ${helperText}`}>
              NICE recommends a minimum of 14 successful daytime readings to
              calculate the daytime average. Use the values from the ABPM
              report.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className={labelCls}>Total readings</Label>
                <Input
                  type="number"
                  value={formData.totalReadings}
                  onChange={(e) =>
                    handleChange("totalReadings", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Successful readings</Label>
                <Input
                  type="number"
                  value={formData.successfulReadings}
                  onChange={(e) =>
                    handleChange("successfulReadings", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div
                className={`text-xs font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Daytime average
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  placeholder="Systolic"
                  value={formData.daytimeAvgSystolic}
                  onChange={(e) =>
                    handleChange("daytimeAvgSystolic", e.target.value)
                  }
                  className={inputCls}
                />
                <Input
                  type="number"
                  placeholder="Diastolic"
                  value={formData.daytimeAvgDiastolic}
                  onChange={(e) =>
                    handleChange("daytimeAvgDiastolic", e.target.value)
                  }
                  className={inputCls}
                />
              </div>

              <div
                className={`text-xs font-medium pt-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                Night-time average
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  placeholder="Systolic"
                  value={formData.nighttimeAvgSystolic}
                  onChange={(e) =>
                    handleChange("nighttimeAvgSystolic", e.target.value)
                  }
                  className={inputCls}
                />
                <Input
                  type="number"
                  placeholder="Diastolic"
                  value={formData.nighttimeAvgDiastolic}
                  onChange={(e) =>
                    handleChange("nighttimeAvgDiastolic", e.target.value)
                  }
                  className={inputCls}
                />
              </div>

              <div
                className={`text-xs font-medium pt-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
              >
                24-hour average
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  placeholder="Systolic"
                  value={formData.twentyFourHourAvgSystolic}
                  onChange={(e) =>
                    handleChange("twentyFourHourAvgSystolic", e.target.value)
                  }
                  className={inputCls}
                />
                <Input
                  type="number"
                  placeholder="Diastolic"
                  value={formData.twentyFourHourAvgDiastolic}
                  onChange={(e) =>
                    handleChange("twentyFourHourAvgDiastolic", e.target.value)
                  }
                  className={inputCls}
                />
              </div>
            </div>

            {/* Derived classification from the daytime average */}
            {(() => {
              const s = parseFloat(formData.daytimeAvgSystolic);
              const d = parseFloat(formData.daytimeAvgDiastolic);
              if (
                isNaN(s) ||
                isNaN(d) ||
                formData.daytimeAvgSystolic === "" ||
                formData.daytimeAvgDiastolic === ""
              ) {
                return null;
              }
              type Cat = {
                label: string;
                badge: string;
                wrapper: string;
                message: string;
              };
              let cat: Cat;
              const isStage2 =
                !(s >= 170 || d >= 115) &&
                ((s >= 150 && s <= 169) || (d >= 95 && d <= 114));
              if (s >= 170 || d >= 115) {
                cat = {
                  label: "VERY HIGH BLOOD PRESSURE",
                  badge: isDarkMode
                    ? "bg-rose-500/30 text-rose-200"
                    : "bg-rose-600 text-white",
                  wrapper: isDarkMode
                    ? "bg-rose-900/20 border-rose-700"
                    : "bg-rose-50 border-rose-300",
                  message:
                    "Severe hypertension on ABPM. Same-day GP referral is recommended.",
                };
              } else if ((s >= 150 && s <= 169) || (d >= 95 && d <= 114)) {
                cat = {
                  label: "STAGE 2 HYPERTENSION",
                  badge: isDarkMode
                    ? "bg-orange-500/30 text-orange-200"
                    : "bg-orange-500 text-white",
                  wrapper: isDarkMode
                    ? "bg-orange-900/20 border-orange-700"
                    : "bg-orange-50 border-orange-300",
                  message:
                    "Stage 2 hypertension confirmed. Refer to GP for treatment review.",
                };
              } else if ((s >= 135 && s <= 149) || (d >= 85 && d <= 94)) {
                cat = {
                  label: "STAGE 1 HYPERTENSION",
                  badge: isDarkMode
                    ? "bg-amber-400/30 text-amber-200"
                    : "bg-amber-400 text-white",
                  wrapper: isDarkMode
                    ? "bg-amber-900/20 border-amber-700"
                    : "bg-amber-50 border-amber-300",
                  message:
                    "ABPM stage 1 hypertension identified. Refer patient to their registered GP practice or another healthcare provider within 3 weeks.",
                };
              } else if (s <= 89 || d <= 59) {
                cat = {
                  label: "LOW BLOOD PRESSURE",
                  badge: isDarkMode
                    ? "bg-blue-500/30 text-blue-200"
                    : "bg-blue-500 text-white",
                  wrapper: isDarkMode
                    ? "bg-blue-900/20 border-blue-700"
                    : "bg-blue-50 border-blue-300",
                  message:
                    "Low blood pressure on ABPM. Review symptoms and consider GP referral.",
                };
              } else {
                cat = {
                  label: "NORMAL BLOOD PRESSURE",
                  badge: isDarkMode
                    ? "bg-emerald-500/30 text-emerald-200"
                    : "bg-emerald-500 text-white",
                  wrapper: isDarkMode
                    ? "bg-emerald-900/20 border-emerald-700"
                    : "bg-emerald-50 border-emerald-300",
                  message:
                    "Daytime average within the normal range. No hypertension confirmed.",
                };
              }
              return (
                <div className="mt-4 space-y-3">
                  <div className={`rounded-lg border p-3 ${cat.wrapper}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div
                        className={`text-sm font-semibold ${
                          isDarkMode ? "text-gray-100" : "text-gray-900"
                        }`}
                      >
                        Daytime average: {s}/{d} mmHg
                      </div>
                      <span
                        className={`rounded-sm px-2 py-0.5 text-[11px] font-semibold tracking-wide ${cat.badge}`}
                      >
                        {cat.label}
                      </span>
                    </div>
                    <div
                      className={`mt-1 text-xs ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      {cat.message}
                    </div>
                  </div>

                  {isStage2 && (
                    <>
                      <Alert
                        className={
                          isDarkMode
                            ? "bg-amber-900/20 border-amber-700"
                            : "bg-amber-50 border-amber-300"
                        }
                      >
                        <AlertDescription
                          className={
                            isDarkMode ? "text-amber-100" : "text-amber-900"
                          }
                        >
                          Check for acute symptoms such as labile or postural
                          hypotension, headache, palpitations, pallor, abdominal
                          pain or diaphoresis.
                        </AlertDescription>
                      </Alert>

                      <div className="space-y-2">
                        <Label className={labelCls}>
                          Does the patient have any acute symptoms?
                        </Label>
                        <RadioGroup
                          value={formData.stage2AcuteSymptoms}
                          onValueChange={(v) =>
                            handleChange(
                              "stage2AcuteSymptoms",
                              v as "yes" | "no",
                            )
                          }
                          className="flex gap-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="yes"
                              id="stage2-symptoms-yes"
                            />
                            <Label
                              htmlFor="stage2-symptoms-yes"
                              className={labelCls}
                            >
                              Yes
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="no"
                              id="stage2-symptoms-no"
                            />
                            <Label
                              htmlFor="stage2-symptoms-no"
                              className={labelCls}
                            >
                              No
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {formData.stage2AcuteSymptoms === "yes" && (
                        <div
                          className={`rounded-lg border p-3 ${
                            isDarkMode
                              ? "bg-rose-900/20 border-rose-700"
                              : "bg-rose-50 border-rose-300"
                          }`}
                        >
                          <div
                            className={`text-sm font-semibold ${
                              isDarkMode ? "text-gray-100" : "text-gray-900"
                            }`}
                          >
                            ABPM stage 2 hypertension with symptoms identified
                          </div>
                          <div
                            className={`mt-1 text-xs ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Patients with any acute symptoms should be advised
                            to see their registered GP practice or another
                            healthcare provider sooner than 7 days and should
                            call the GP during opening hours and while the
                            patient is still in the pharmacy.
                          </div>
                        </div>
                      )}

                      {formData.stage2AcuteSymptoms === "no" && (
                        <div
                          className={`rounded-lg border p-3 ${
                            isDarkMode
                              ? "bg-orange-900/20 border-orange-700"
                              : "bg-orange-50 border-orange-300"
                          }`}
                        >
                          <div
                            className={`text-sm font-semibold ${
                              isDarkMode ? "text-gray-100" : "text-gray-900"
                            }`}
                          >
                            ABPM stage 2 hypertension identified
                          </div>
                          <div
                            className={`mt-1 text-xs ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Refer patient to their registered GP practice or
                            another healthcare provider within 7 days.
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>

          {/* ── Section 6: Referral Details ──────────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>6</div>
              <h3 className={sectionTitle}>Referral Details</h3>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className={labelCls}>Refer to</Label>
                <RadioGroup
                  value={formData.referralTarget}
                  onValueChange={(v) => {
                    handleChange("referralTarget", v as any);
                    if (v === "gp") {
                      handleChange("referralOtherType", "");
                      handleChange("referralOtherSpecify", "");
                    }
                  }}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="gp"
                      id="ref-gp"
                      className={radioCls}
                    />
                    <label htmlFor="ref-gp" className={radioLabelCls}>
                      GP
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="other"
                      id="ref-other"
                      className={radioCls}
                    />
                    <label htmlFor="ref-other" className={radioLabelCls}>
                      Other
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {formData.referralTarget === "gp" && (
                <div
                  className={`rounded-xl border p-3 ${
                    isDarkMode
                      ? "border-gray-700 bg-gray-800/40"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <Label className={labelCls}>Patient&apos;s GP Surgery</Label>
                  {gpSurgery ? (
                    <div className="mt-2 space-y-1">
                      <div
                        className={`text-sm font-semibold ${
                          isDarkMode ? "text-gray-100" : "text-gray-900"
                        }`}
                      >
                        {gpSurgery.name}
                      </div>
                      <div className={`text-xs ${helperText}`}>
                        {gpSurgery.address}
                      </div>
                      <div className={`text-xs ${helperText}`}>
                        Tel: {gpSurgery.phone}
                      </div>
                      <div className="pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setGpDraft(gpSurgery);
                            setGpAddMode(true);
                            setGpSurgery(null);
                          }}
                        >
                          Change
                        </Button>
                      </div>
                    </div>
                  ) : gpAddMode ? (
                    <div className="mt-2 space-y-2">
                      <Input
                        placeholder="Surgery name"
                        value={gpDraft.name}
                        onChange={(e) =>
                          setGpDraft({ ...gpDraft, name: e.target.value })
                        }
                        className={inputCls}
                      />
                      <Input
                        placeholder="Address"
                        value={gpDraft.address}
                        onChange={(e) =>
                          setGpDraft({ ...gpDraft, address: e.target.value })
                        }
                        className={inputCls}
                      />
                      <Input
                        placeholder="Telephone"
                        value={gpDraft.phone}
                        onChange={(e) =>
                          setGpDraft({ ...gpDraft, phone: e.target.value })
                        }
                        className={inputCls}
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={isUpdatePatientSurgeryLoading}
                          onClick={async () => {
                            if (gpDraft.name.trim()) {
                              setGpSurgery(gpDraft);
                              setGpAddMode(false);
                              const payLoad = {
                                patientId:
                                  selectedPatientDetails?.patientID || "",
                                name: gpDraft.name,
                                address: gpDraft.address,
                                contactTelephoneNumber: gpDraft.phone,
                              };
                              await updatePatientSurgery(payLoad);
                            }
                          }}
                        >
                          {isUpdatePatientSurgeryLoading
                            ? "Please wait..."
                            : "Save GP Surgery"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setGpAddMode(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 space-y-2">
                      <Alert
                        className={
                          isDarkMode
                            ? "bg-amber-900/20 border-amber-700 text-amber-100"
                            : "bg-amber-50 border-amber-300 text-amber-900"
                        }
                      >
                        <Info className="h-4 w-4" />
                        <AlertDescription className="text-sm">
                          No GP surgery on file for this patient.
                        </AlertDescription>
                      </Alert>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setGpDraft({ name: "", address: "", phone: "" });
                            setGpAddMode(true);
                          }}
                        >
                          Add one
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={handlePdsSync}
                          disabled={pdsSyncing}
                        >
                          {pdsSyncing ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Syncing…
                            </>
                          ) : (
                            "Use PDS Sync"
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {formData.referralTarget === "other" && (
                <div
                  className={`rounded-xl border p-3 space-y-3 ${
                    isDarkMode
                      ? "border-gray-700 bg-gray-800/40"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  <Label className={labelCls}>Refer to</Label>
                  <RadioGroup
                    value={formData.referralOtherType}
                    onValueChange={(v) =>
                      handleChange("referralOtherType", v as any)
                    }
                    className="space-y-2"
                  >
                    {[
                      { value: "ooh-gp", label: "Out of hours GP" },
                      { value: "ae", label: "A&E" },
                      { value: "999", label: "999" },
                      { value: "other", label: "Other (please specify)" },
                    ].map((opt) => (
                      <div
                        key={opt.value}
                        className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer ${
                          formData.referralOtherType === opt.value
                            ? isDarkMode
                              ? "border-rose-500 bg-rose-900/20"
                              : "border-rose-400 bg-rose-50"
                            : isDarkMode
                              ? "border-gray-600"
                              : "border-gray-200"
                        }`}
                        onClick={() =>
                          handleChange("referralOtherType", opt.value as any)
                        }
                      >
                        <RadioGroupItem
                          value={opt.value}
                          id={`ref-other-${opt.value}`}
                          className={radioCls}
                        />
                        <label
                          htmlFor={`ref-other-${opt.value}`}
                          className={`${radioLabelCls} cursor-pointer`}
                        >
                          {opt.label}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>

                  {formData.referralOtherType === "other" && (
                    <div className="space-y-2">
                      <Label className={labelCls}>Please specify</Label>
                      <Input
                        value={formData.referralOtherSpecify}
                        onChange={(e) =>
                          handleChange("referralOtherSpecify", e.target.value)
                        }
                        placeholder="Specify referral destination"
                        className={inputCls}
                      />
                    </div>
                  )}
                </div>
              )}

              {formData.referralTarget !== "" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className={labelCls}>Referral date</Label>
                    <div className="relative">
                      <Input
                        type="date"
                        value={formData.referralDate}
                        onChange={(e) =>
                          handleChange("referralDate", e.target.value)
                        }
                        className={inputCls}
                      />
                      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className={labelCls}>Referral time</Label>
                    <Input
                      type="time"
                      value={formData.referralTime}
                      onChange={(e) =>
                        handleChange("referralTime", e.target.value)
                      }
                      className={inputCls}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Section 7: Healthy Living Advice ───────────────────────── */}
          <div className={card}>
            <div className="flex items-center gap-3 mb-4">
              <div className={sectionBadge}>7</div>
              <h3 className={sectionTitle}>Healthy Living Advice</h3>
            </div>
            <p className={`mb-3 ${helperText}`}>
              Select the healthy living advice areas you covered with the
              patient during this consultation.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "diet-nutrition", label: "Diet & nutrition" },
                { value: "sodium-salt", label: "Sodium / salt" },
                { value: "caffeine", label: "Caffeine" },
                { value: "alcohol", label: "Alcohol" },
                { value: "smoking", label: "Smoking" },
                { value: "physical-activity", label: "Physical activity" },
                {
                  value: "referral-local-healthy-living",
                  label: "Referral to a local Healthy Living service",
                },
              ].map((opt) => {
                const checked = (
                  formData.healthyLivingAdvice as string[]
                ).includes(opt.value);
                return (
                  <label
                    key={opt.value}
                    htmlFor={`htn-fu-hla-${opt.value}`}
                    className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                      checked
                        ? isDarkMode
                          ? "border-blue-500 bg-blue-900/20"
                          : "border-blue-500 bg-blue-50"
                        : isDarkMode
                          ? "border-gray-600 bg-gray-700"
                          : "border-gray-300 bg-white"
                    }`}
                  >
                    <Checkbox
                      id={`htn-fu-hla-${opt.value}`}
                      checked={checked}
                      onCheckedChange={() => {
                        const current = formData.healthyLivingAdvice;
                        const next = current.includes(opt.value as any)
                          ? current.filter((v) => v !== opt.value)
                          : [...current, opt.value as any];
                        handleChange("healthyLivingAdvice", next);
                      }}
                    />
                    <span className={radioLabelCls}>{opt.label}</span>
                  </label>
                );
              })}
            </div>
            <div className="space-y-2 mt-4">
              <Label htmlFor="htn-fu-hla-notes" className={labelCls}>
                Additional notes (optional)
              </Label>
              <Textarea
                id="htn-fu-hla-notes"
                rows={2}
                value={formData.healthyLivingAdviceNotes}
                onChange={(e) =>
                  handleChange("healthyLivingAdviceNotes", e.target.value)
                }
                className={inputCls}
              />
            </div>
          </div>

          <div className={card}>
            <div className="space-y-2">
              <Label className={labelCls}>Internal notes</Label>
              <Textarea
                rows={3}
                value={formData.internalNotes}
                onChange={(e) => handleChange("internalNotes", e.target.value)}
                className={inputCls}
              />
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="submit"
          disabled={
            nhsHypertensionABPMFolloupFormLoading ||
            consultationStatus === 1 ||
            !canSubmit
          }
        >
          {nhsHypertensionABPMFolloupFormLoading
            ? "Please wait..."
            : "Complete Stage 3"}
        </Button>
      </div>
    </form>
  );
}
